import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, Typography, Box, Stack, Button, Divider, Grid } from '@mui/material';
import { format } from 'date-fns';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import EscalationPriorityBadge from './EscalationPriorityBadge';
import { getEscalationById } from '../../../services/escalationService';
import Loading from '../../../layouts/loading';

const queryClient = new QueryClient();

const EscalationDetailContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: escalationResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['escalation', id],
    queryFn: async () => {
      const response = await getEscalationById(id);
      return response?.data?.data;
    },
    enabled: !!id,
  });

  const escalation = escalationResponse;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Escalations',
      path: '/escalations',
    },
    {
      label: 'Escalation Details',
      path: '',
    },
  ];

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  if (error || !escalation) {
    return (
      <>
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error" align="center">
                {error?.response?.data?.message || 'Error loading escalation details'}
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                  onClick={() => navigate('/escalations')}
                >
                  Back to Escalations
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Escalation Details</title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Escalation Details</Typography>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/escalations')}
          >
            Back to Escalations
          </Button>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              {/* Header with Severity */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Escalation Information</Typography>
                <EscalationPriorityBadge severity={escalation.severity} />
              </Box>

              <Divider />

              {/* Details Grid */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Project
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {escalation.project?.projectName || `Project #${escalation.projectId}`}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Severity
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <EscalationPriorityBadge severity={escalation.severity} />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {escalation.description || 'No description provided'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {escalation.creator?.userName || escalation.creator?.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(escalation.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(escalation.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

const EscalationDetail = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <EscalationDetailContent />
    </QueryClientProvider>
  );
};

export default EscalationDetail;
